import { Box, Button } from '@mui/material';
import { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { take } from 'rxjs';

import {
  C_Global_Error_Code,
  Wa_CreateArticleInput,
} from '../../../../../graphql/generatedModel.ts';
import { responseHandler } from '../../../../../shared/responseHandler/responseHandler.ts';
import { modeService } from '../../../../../shared/services/mode/mode.service.ts';
import { articleService, CreateOrCopyArticleRes } from '../../../services/article.service.ts';
import { articleTabLoadingService } from '../articleTababLoading.service.ts';
import { IGeneralTabProps } from './general.tab.tsx';
import { GeneralForm } from './components/generalForm.component.tsx';
import { ITabState } from '../../../states/tabState.model.ts';

export const GeneralCopyTab: FC<IGeneralCopyTabProps> = ({
  mode,
  isDough,
  loading,
  initSpecialPrices,
  pricesByCustomer,
  resellerPrices,
  originalArticleId,
}) => {
  const { t } = useTranslation();
  const {
    formState: { dirtyFields },
    getValues,
    handleSubmit,
    reset,
  } = useFormContext();

  const onSubmitForm = () => {
    const { id: _id, descriptionTranslations: _descriptionTranslations, ...data } = getValues();
    if (originalArticleId && data.articleNo && dirtyFields['articleNo']) {
      articleTabLoadingService.pub.loading(true);
      const d = data as Wa_CreateArticleInput;
      articleService.pub.copyArticle({ originalId: originalArticleId, data: d });
    }
  };

  const onCancelCopyMode = () => {
    // keep this reset() for server validation handler
    // this keepDirty blocks pub.save() in handleSubmitValue from being called again after copying or creating a new entry
    reset(undefined, { keepDirty: false });
    modeService.pub.mode('edit');
  };

  useEffect(() => {
    const copyArticleSub = articleService.sub
      .copyArticle()
      .pipe(
        take(1),
        responseHandler<CreateOrCopyArticleRes | undefined>({
          success: () => 'article.article_saved',
          customErrorHandler: (err) => {
            articleTabLoadingService.pub.loading(false);
            // Bad-input outcomes here (the source article was deleted before the
            // copy, or the new article number already exists) are expected user/
            // data conditions, not bugs — show the snackbar but don't report them
            // to Sentry (which otherwise auto-files Jira tickets, e.g. WA-3668).
            // Genuine server errors keep the default reportToSentry: true.
            const isBadUserInput = err.code === C_Global_Error_Code.GEC30_BAD_USER_INPUT;
            return {
              message: 'article.article_not_saved',
              reportToSentry: !isBadUserInput,
            };
          },
        }),
      )
      .subscribe(() => articleTabLoadingService.pub.loading(false));

    return () => {
      copyArticleSub.unsubscribe();
    };
  }, []);

  return (
    <>
      <GeneralForm
        loading={loading}
        mode={mode}
        isDough={isDough}
        articleSpecialPrices={initSpecialPrices || []}
        articlePricesByCustomers={pricesByCustomer}
        articleResellerPrices={resellerPrices}
      />
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          gap: 2,
        }}
      >
        <Button variant='outlined' onClick={onCancelCopyMode}>
          {t('common.cancel')}
        </Button>
        <Button type='submit' variant='contained' onClick={handleSubmit(onSubmitForm)}>
          {t('article.copy_article')}
        </Button>
      </Box>
    </>
  );
};

interface IGeneralCopyTabProps extends Pick<IGeneralTabProps, 'mode' | 'isDough' | 'loading'> {
  initSpecialPrices: ITabState['articleSpecialPrices'] | null;
  pricesByCustomer: ITabState['articlePricesByCustomers'];
  resellerPrices: ITabState['articleResellerPrices'];
  originalArticleId: ITabState['defaultValues']['id'];
}
